import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../shared/ss-select-tooltip.directive';
import { map, type Observable } from 'rxjs';

import { normalizeTableCellConfig, type TableCellConfig } from '../../../chart-components/unified-table-matrix-chart/table-cell-config';
import { DvmBuilderStepBase, type TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile } from '../../dvm-layout-builder/dvm-layout-builder.types';
import type { AggregationOption, AggregationParamSpec } from '../../shared/self-service-config.types';
import { rebaseSelfServiceApiUrl, resolveSelfServiceApiUrl } from '../../shared/self-service-api.utils';
import { SelfServiceDatasetService } from '../../shared/self-service-dataset.service';
import { SelfServiceUiService } from '../../shared/self-service-ui.service';
import { distinctDropdownOptions } from '../dropdown-step/dropdown-options.utils';
import { TableCellConfigEditorComponent } from '../shared/table-cell-config-editor.component';
import { rowsByLevel } from '../nested-table/nested-table.utils';
import type { NestedSortKey, NestedTableLevelQuery, NestedTableQueryFilter, NestedTableRow } from '../nested-table/nested-table.types';
import { sortKeyToRule, sortRuleToKey, type NestedSortRule } from '../nested-table/nested-table-sort.utils';
import { nestedQueryRows } from '../nested-table/nested-table-query.utils';
import { nestedTableData, nestedTableRows } from './nested-table-tile.utils';
import { EXCEL_EXPORT_DEFAULT_BODY, EXCEL_EXPORT_DEFAULT_URL } from '../../shared/platform-excel-export.utils';
import {
  nestedDatasetColumnOptions,
  nestedDatasetColumnValue,
  parseNestedDatasetColumnValue,
  type NestedDatasetColumnOption,
} from './nested-table-column-options.utils';

/** One column shown at a level — like the Normal table's column rows, it can
 *  read from a DIFFERENT dataset than the primary one (the backend joins). */
interface NestedColumnForm {
  id: string;
  /** data = a dataset column; input / dropdown = an editable column the table
   *  adds itself (no dataset needed — what users type is sent by action buttons). */
  kind: NestedColumnKind;
  /** This column carries the level's expand arrow (else the first column). */
  expander?: boolean;
  /** Show this row's serial number beside this column's value. */
  serialNumber?: boolean;
  /** Not drawn in the table, but still requested — so its value can be
   *  sent as a filter ("Filter by parent row", navigation, row tokens). */
  hidden?: boolean;
  /** dropdown: its choices, comma-separated. */
  options?: string;
  /** input / dropdown: must be filled before the row's Save shows. */
  required?: boolean;
  /** input / dropdown: shown as a box but cannot be changed. */
  readOnly?: boolean;
  /** input: restrict edits to numeric characters when enabled. */
  numericOnly?: boolean;
  /** input: lowest / highest value allowed - a number or a row column. */
  minValue?: string;
  maxValue?: string;
  /** % added to the min / max (signed, e.g. -10 / 10; blank = 0). */
  minOffsetPct?: number;
  maxOffsetPct?: number;
  /** Several min / max columns: the lowest or the highest of them is used. */
  minPick?: 'lowest' | 'highest';
  maxPick?: 'lowest' | 'highest';
  /** false = the limits only on hover, not under the field. */
  showBounds?: boolean;
  /** Message box under an out-of-range value; blank = no message (only the red border). */
  rangeMessage?: string;
  /** Blank = the primary dataset. */
  dataset: string;
  field: string;
  /** ONE column from TWO database columns ("6% - 10%"): the second column of
   *  the same dataset (data) or the second key users fill in (input / dropdown).
   *  Blank = an ordinary single-value column. */
  secondField?: string;
  /** Text between the two values. Blank = "-". */
  separator?: string;
  /** Header text; blank = the field name. */
  label: string;
  /** Generic cell configuration (shared editor). */
  cellConfig?: TableCellConfig;
  /** Formula columns have no dataset field; their header is the output alias. */
  formula?: string;
  /** Round off: decimal places numeric values are shown with. Blank = as is. */
  decimals?: number;
  /** Number scale: auto / K / M / B. Blank = the full number. */
  scale?: 'auto' | 'K' | 'M' | 'B';
  /** Column width in px. Blank = automatic. */
  width?: number;
  /** Shown × the dashboard's exchange rate (display only). */
  convertCurrency?: boolean;
  /** Aggregation is opt-in so dimensions remain ordinary group-by columns. */
  aggregate?: boolean;
  aggregation?: string;
  /** The picked aggregation's own settings (e.g. ALL_MATCH's matchValue), by key. */
  aggregationParams?: Record<string, string | boolean>;
  /** Filters scoped to this selected column in the compute payload. */
  filters: NestedFilterForm[];
}

type NestedColumnKind = 'data' | 'formula' | 'input' | 'dropdown' | 'serial';

interface NestedFilterForm {
  id: string;
  dataset: string;
  field: string;
  operator: string;
  value: string;
}

/** Where every level's child rows are stored — never a real column, so a column
 *  is never overwritten by its children (that showed as [object Object]). */
const CHILDREN_FIELD = 'children';

/** One button above a level's table (Export / navigate / API), added in order. */
interface NestedButtonForm {
  id: string;
  label: string;
  style: 'outline' | 'filled';
  action: 'export' | 'excel' | 'navigate' | 'api';
  /** excel: the sheet's name. */
  sheetName: string;
  /** excel: dataset columns sent as filters (empty = every dashboard selection). */
  filterColumns: string[];
  navigateType: 'url' | 'dashboard' | 'route';
  navigateTarget: string;
  navigateNewTab: boolean;
  apiMethod: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  apiUrl: string;
  apiBody: string;
}

/** One expandable level of the table. */
interface NestedLevelForm {
  id: string;
  title: string;
  /** Row field holding this level's children. Empty on the deepest level. */
  childrenField: string;
  /** Columns shown at this level; empty means infer from the rows. */
  columns: NestedColumnForm[];
  /** Buttons above this level's table, left to right in the order added. */
  buttons: NestedButtonForm[];
  /** A Save button on edited rows, calling this API. */
  rowSave: { enabled: boolean; label: string; url: string; entity: string; businessKeys: string[]; payloadColumns: string[] };
  /** Filters applied to the whole level request. */
  filters: NestedFilterForm[];
  /** Columns of the levels above: each one is sent as `column = value of the
   *  row that was expanded` (as `[column]`, resolved at expand time). */
  parentKeys: string[];
  sortField: string;
  sortDirection: 'ASC' | 'DESC';
  /** Row order applied in the table: rule 1, then rule 2 for ties ... (a rule with several columns sorts on their sum). */
  sortRules?: NestedSortRule[];
  /** Opt-in: clicking a header of this level sorts by it. */
  headerSort?: boolean;
  paginationEnabled: boolean;
}

/**
 * Nested Table build form. Every level is configured as an independent TABLE
 * request; the first loads on Generate and deeper levels load on expansion.
 * A filter value such as `[customer]` is resolved from the clicked row/parents.
 */
@Component({
  selector: 'app-nested-table-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, NgSelectModule, SsSelectTooltipDirective, TableCellConfigEditorComponent],
  templateUrl: './nested-table-step.component.html',
  // Scoped to the component rather than the builder stylesheet, because this
  // step renders in BOTH the Layout Builder and the Design Builder's data tab.
  // Same card language as .dropdown-group-card: the form column is too narrow
  // for side-by-side fields once a multi-select's chips are in it.
  styles: [`
    :host { display: contents; }

    .nested-level-card {
      margin-top: 8px;
      padding: 8px 10px 10px;
      border: 1px solid rgba(119, 144, 179, .3);
      border-radius: 10px;
      background: rgba(255, 255, 255, .6);
    }
    .nested-level-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 6px;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: .04em;
      text-transform: uppercase;
      color: #6d7f99;
    }
    .nested-level-remove {
      padding: 0 4px;
      border: none;
      background: transparent;
      color: #b0392f;
      font-size: 16px;
      line-height: 1;
      cursor: pointer;
    }
    .nested-level-field {
      margin-top: 6px;

      label {
        display: block;
        margin-bottom: 3px;
        font-size: 11px;
        font-weight: 600;
        color: #4e5f78;
      }
      input { width: 100%; }
      .ss-select { width: 100%; }
    }
    .nested-level-add,
    .nested-column-add {
      width: 100%;
      margin-top: 8px;
      padding: 7px;
      border: 1px dashed rgba(119, 144, 179, .55);
      border-radius: 10px;
      background: transparent;
      color: #4e5f78;
      font: inherit;
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
    }
    .nested-column-add { margin-top: 6px; padding: 5px; border-radius: 8px; }
    .nested-level-add:hover:not(:disabled),
    .nested-column-add:hover { border-color: #1f6feb; color: #1f6feb; }
    .nested-level-add:disabled { opacity: .45; cursor: not-allowed; }

    /* Per-column rows (Dataset / Column / Header + cell formatting). */
    .nested-column-card {
      margin-top: 8px;
      padding: 10px 10px 12px;
      border: 1px solid rgba(119, 144, 179, .25);
      border-radius: 8px;
      background: rgba(248, 250, 253, .9);
    }
    .nested-column-head {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 8px;
      font-size: 11px;
      font-weight: 600;
      color: #344054;
    }
    .nested-column-head .nested-column-title { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .nested-column-head .nested-column-dataset { font-weight: 500; color: #98a2b3; }
    /* Breathing room inside a column card: the stacked tick boxes and the
       dataset / second-column pickers read as one crowded block otherwise. */
    .nested-column-card .nested-level-field { margin-top: 10px; }
    .nested-column-card .nested-level-field label { margin-bottom: 5px; }
    .nested-column-config { margin-top: 10px; }
    .nested-kind { width: 100%; }
    .nested-expander { display: flex; align-items: flex-start; gap: 8px; margin-top: 10px; font-size: 11px; font-weight: 600; line-height: 1.35; color: #4e5f78; cursor: pointer; }
    .nested-expander + .nested-expander { margin-top: 8px; }
    .nested-expander + .nested-level-field { margin-top: 12px; }
    /* A normal small tick box: the form's input rules (full width, tall, padded)
       otherwise blew the checkbox up into a big square. */
    .nested-expander input[type='checkbox'] {
      flex: 0 0 auto;
      width: 14px !important;
      min-width: 14px !important;
      height: 14px !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      accent-color: #1f6feb;
      cursor: pointer;
    }
    .nested-column-kind { font-weight: 500; color: #6d7f99; }
    .nested-column-move {
      padding: 0 3px;
      border: none;
      background: transparent;
      color: #4e5f78;
      font-size: 13px;
      line-height: 1;
      cursor: pointer;
    }
    .nested-column-move:hover:not(:disabled) { color: #1f6feb; }
    .nested-column-move:disabled { opacity: .3; cursor: default; }
  `],
})
export class NestedTableStepComponent extends DvmBuilderStepBase {
  readonly maxLevels = MAX_NESTED_LEVELS;
  readonly layoutTypes = ['NESTED_TABLE'] as const;

  /** Path to the rows inside the response, when they are not at the root. */
  rowsKey = '';
  subtitle = '';
  levels: NestedLevelForm[] = [newLevel('Level 1', 'children'), newLevel('Level 2', '')];
  readonly filterOperators = ['=', '!=', '>', '>=', '<', '<=', 'IN', 'LIKE'];
  private readonly http = inject(HttpClient);
  private readonly ui = inject(SelfServiceUiService);

  /** The Table & Matrix type list (this form is its "Nested Table" type). */
  get tableTypes(): Array<{ label: string; value: string; group?: string }> {
    const configured = (this.host.layout.options as Record<string, unknown> | undefined)?.['tableTypes'];
    return Array.isArray(configured) && configured.length
      ? configured as Array<{ label: string; value: string; group?: string }>
      : [
          { label: 'Normal Table', value: 'normal', group: 'Standard' },
          { label: 'Editable Table', value: 'editable', group: 'Interactive' },
          { label: 'Drill-Down Table', value: 'drilldown', group: 'Interactive' },
          { label: 'Nested Table', value: 'nested', group: 'Interactive' },
        ];
  }

  onTableTypeChange(type: string | null): void {
    if (type && type !== 'nested') this.host.switchTableType(type);
  }

  /** Columns of the PRIMARY dataset (the "Expands into" field lives on its rows). */
  get columnOptions(): string[] {
    return this.host.drillDatasetColumns ?? [];
  }

  /** The deepest level has nothing to expand into, so it hides that field. */
  isLastLevel(index: number): boolean {
    return index === this.levels.length - 1;
  }

  addLevel(): void {
    if (this.levels.length >= MAX_NESTED_LEVELS) return;
    // The level that was last now expands into the new one.
    const previous = this.levels[this.levels.length - 1];
    if (!previous.childrenField) previous.childrenField = 'children';
    this.levels = [...this.levels, newLevel(`Level ${this.levels.length + 1}`, '')];
    this.host.invalidatePreview();
  }

  removeLevel(index: number): void {
    if (this.levels.length <= 1) return;
    this.levels = this.levels.filter((_, position) => position !== index);
    // Whatever is last can no longer expand.
    this.levels[this.levels.length - 1].childrenField = '';
    this.host.invalidatePreview();
  }

  onFieldChange(): void {
    this.host.invalidatePreview();
    // Keep the existing canvas tile in sync with display-only column changes
    // (such as a serial number) without re-running the dataset query.
    const tileId = this.host.editingTileId;
    const tile = tileId ? this.host.findTileAnywhere(tileId) : undefined;
    if (!tile?.nestedTableData) return;
    const payload = this.buildPayload();
    const data = nestedTableData(
      tile.nestedTableData.staticData ?? tile.nestedTableData.rows ?? [],
      payload,
      tile.title,
      tile.format ?? {},
    );
    data.widgetId = tile.nestedTableData.widgetId;
    this.host.updateTileInPlace({ ...tile, payload, nestedTableData: data });
  }

  // ── Per-column rows (cross-dataset, like the Normal table's column cards) ──

  /** The dataset a column row reads from (blank = the primary dataset). */
  columnDataset(column: NestedColumnForm): string {
    return column.dataset || this.host.drillDataset;
  }
  private readonly datasetService = inject(SelfServiceDatasetService);
  private readonly columnValueLoaders = new Map<string, () => Observable<string[]>>();
  private levelRowsMemo: { source: NestedTableRow[]; byLevel: NestedTableRow[][] } | null = null;
  private readonly columnValuesMemo = new WeakMap<NestedColumnForm, { rows: unknown; field: string; items: string[] }>();
  /** Distinct values of a column at its level — from the generated table's
   *  own rows (the tile being edited) when it has any, else the dataset's
   *  cached sample rows. Undefined when neither is at hand yet, in which case
   *  the editor offers "From dataset" (the loader below). Memoized per column
   *  so the editor gets a stable list. */
  columnValues(level: NestedLevelForm, column: NestedColumnForm): string[] | undefined {
    if (column.kind !== 'data' || !column.field) return undefined;
    let rows: Array<Record<string, unknown>> | undefined;
    const tile = this.host.editingTileId ? this.host.findTileAnywhere(this.host.editingTileId) : undefined;
    const data = tile?.nestedTableData;
    const source = data?.staticData ?? data?.rows;
    if (data && Array.isArray(source) && source.length) {
      if (this.levelRowsMemo?.source !== source) this.levelRowsMemo = { source, byLevel: rowsByLevel(data, source) };
      rows = this.levelRowsMemo.byLevel[this.levels.indexOf(level)];
    }
    if (!rows?.length) rows = this.host.cachedRows(this.columnDataset(column));
    if (!rows?.length) return undefined;
    const memo = this.columnValuesMemo.get(column);
    if (memo && memo.rows === rows && memo.field === column.field) return memo.items;
    const items = distinctDropdownOptions(rows, column.field);
    this.columnValuesMemo.set(column, { rows, field: column.field, items });
    return items;
  }
  /** Stable per-column loader of the column's distinct dataset values
   *  (Cell formatting → Value colours → "From dataset"). */
  columnValuesLoader(column: NestedColumnForm): (() => Observable<string[]>) | null {
    const dataset = this.columnDataset(column);
    if (column.kind !== 'data' || !column.field || !dataset) return null;
    const key = `${dataset}\u0000${column.field}`;
    let loader = this.columnValueLoaders.get(key);
    if (!loader) {
      const url = resolveSelfServiceApiUrl(this.host.layout.apiEndpoints?.filterApi, '/getData');
      const field = column.field;
      loader = () => this.datasetService.getDistinctFieldValues(url, dataset, field).pipe(map((rows) => distinctDropdownOptions(rows, field)));
      this.columnValueLoaders.set(key, loader);
    }
    return loader;
  }
  /** A dataset's columns INCLUDING uuid and the system attributes - a nested table addresses
   *  individual records (row save, approval), so it needs its ID column. Memoized per row. */
  readonly excelDefaultUrl = EXCEL_EXPORT_DEFAULT_URL;
  /** Export's "Filter columns": every column of the table's dataset (system attributes too). */
  excelFilterColumnOptions(button: NestedButtonForm): string[] {
    return this.datasetColumns(`excel-${button.id}`, this.host.drillDataset);
  }
  private datasetColumns(rowId: string, dataset: string): string[] {
    return this.host.allColumnsForRow?.(rowId, dataset) ?? this.host.columnsForRow(rowId, dataset);
  }
  /** Columns of the row's dataset (memoized per row by the host). */
  columnsForColumn(column: NestedColumnForm): string[] {
    return this.datasetColumns(column.id, this.columnDataset(column));
  }
  isColumnLoading(column: NestedColumnForm): boolean {
    return this.host.isDatasetColumnsLoading(this.columnDataset(column));
  }
  columnPlaceholder(column: NestedColumnForm): string {
    if (!this.columnDataset(column)) return 'Select a dataset first';
    return this.isColumnLoading(column) ? 'Loading columns...' : 'Select column';
  }

  /** Every field from every dataset selected at component level, grouped by
   * dataset in the UI. The column still stores one exact {dataset, field}.
   *
   * Memoized per column (same `{ key, items }` pattern as `parentFields`):
   * ng-select rebuilds and re-renders its whole option list whenever the
   * `[items]` reference changes, and this getter runs on every change
   * detection pass. Without a stable reference every hover re-created the
   * option under the cursor, so clicks landed on a replaced node and the
   * value never got selected. */
  combinedColumnOptions(column: NestedColumnForm): NestedDatasetColumnOption[] {
    const datasets = Array.from(new Set([
      ...this.host.selectedDatasets,
      ...(column.dataset ? [column.dataset] : []),
    ].filter(Boolean)));
    const columnsByDataset = datasets.map((dataset) => this.datasetColumns(`${column.id}:${dataset}`, dataset));
    const key = datasets.map((dataset, index) => `${dataset}\u0001${columnsByDataset[index].join('\u0000')}`).join('\u0002');
    const memo = this.combinedColumnOptionsMemo.get(column.id);
    if (memo?.key === key) return memo.items;
    const items = nestedDatasetColumnOptions(datasets, (dataset) => columnsByDataset[datasets.indexOf(dataset)]);
    this.combinedColumnOptionsMemo.set(column.id, { key, items });
    return items;
  }
  private readonly combinedColumnOptionsMemo = new Map<string, { key: string; items: NestedDatasetColumnOption[] }>();

  combinedColumnValue(column: NestedColumnForm): string | null {
    return column.field ? nestedDatasetColumnValue(this.columnDataset(column), column.field) : null;
  }

  onCombinedColumnChange(level: NestedLevelForm, column: NestedColumnForm, value: string | null): void {
    const selected = parseNestedDatasetColumnValue(value ?? '');
    column.dataset = selected?.dataset ?? '';
    column.field = selected?.field ?? '';
    column.secondField = '';
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  combinedColumnsLoading(): boolean {
    return this.host.selectedDatasets.some((dataset) => this.host.isDatasetColumnsLoading(dataset));
  }
  /** Fields of a level, for rules that test another column (stable array per level). */
  private readonly levelFieldsMemo = new Map<string, { key: string; items: string[] }>();

  /** Navigation can send this level's columns AND those of the levels above
   *  (a spec row also knows its customer). Memoised like levelFields. */
  navigationFields(level: NestedLevelForm): string[] {
    const index = this.levels.indexOf(level);
    const items = Array.from(new Set(this.levels.slice(0, index + 1)
      .flatMap((item) => item.columns.flatMap((column) => [column.field, column.secondField ?? '']))
      .filter(Boolean)));
    const key = items.join('\u0000');
    const memo = this.navigationFieldsMemo.get(level.id);
    if (memo?.key === key) return memo.items;
    this.navigationFieldsMemo.set(level.id, { key, items });
    return items;
  }
  private readonly navigationFieldsMemo = new Map<string, { key: string; items: string[] }>();
  /** Columns of the levels ABOVE this one — the choices of the level's
   *  "Filter by parent row" dropdown. */
  parentFields(level: NestedLevelForm): string[] {
    const index = this.levels.indexOf(level);
    const items = Array.from(new Set(this.levels.slice(0, Math.max(index, 0))
      .flatMap((item) => item.columns.filter((column) => column.kind === 'data')
        .flatMap((column) => [column.field, column.secondField ?? '']))
      .filter(Boolean)));
    const key = items.join('\u0000');
    const memo = this.parentFieldsMemo.get(level.id);
    if (memo?.key === key) return memo.items;
    this.parentFieldsMemo.set(level.id, { key, items });
    return items;
  }
  private readonly parentFieldsMemo = new Map<string, { key: string; items: string[] }>();
  setHidden(column: NestedColumnForm, hidden: boolean): void {
    column.hidden = hidden || undefined;
    this.onFieldChange();
  }
  /** "Column type" choices (a stable array: ng-select reloads on every new
   *  `[items]` reference). */
  readonly columnKinds: { value: NestedColumnKind; label: string }[] = [
    { value: 'data', label: 'Data column (from a dataset)' },
    { value: 'formula', label: 'Formula column' },
    { value: 'input', label: 'Input box (users type a value)' },
    { value: 'dropdown', label: 'Dropdown (users pick a value)' },
  ];
  /** Row markers are configured separately from a column's data/editor type. */
  readonly rowMarkerOptions = [
    { value: 'none', label: 'None' },
    { value: 'serial', label: 'Serial number' },
  ];
  /** "Round off" choices; null = show the value as it comes. */
  readonly roundOffOptions = [
    { value: 0, label: 'Whole number (0 decimals)' },
    { value: 1, label: '1 decimal' },
    { value: 2, label: '2 decimals' },
    { value: 3, label: '3 decimals' },
  ];
  /** "Number scale" choices; null = the full number. */
  readonly scaleOptions = [
    { value: 'auto', label: 'Auto (K / M / B by size)' },
    { value: 'K', label: 'Thousands (K)' },
    { value: 'M', label: 'Millions (M)' },
    { value: 'B', label: 'Billions (B)' },
  ];
  /** ng-select cannot bind a `null` option value, so the "As is" / "Full
   *  number" rows of the Round off and Number scale pickers carry a sentinel
   *  that the setters map back to "unset". Stable arrays: ng-select reloads on
   *  every new `[items]` reference. */
  readonly noRounding = -1;
  readonly noScale = 'none';
  readonly roundOffChoices: { value: number; label: string }[] = [
    { value: this.noRounding, label: 'As is (no rounding)' },
    ...this.roundOffOptions,
  ];
  readonly scaleChoices: { value: string; label: string }[] = [
    { value: this.noScale, label: 'Full number (23,037,142)' },
    ...this.scaleOptions,
  ];
  /** Button "When clicked" / "Navigate to" rows (stable arrays for ng-select). */
  readonly buttonActionOptions: { value: NestedButtonForm['action']; label: string }[] = [
    { value: 'navigate', label: 'Navigate' },
    { value: 'api', label: 'Call an API' },
    { value: 'export', label: 'Export this table (CSV)' },
    { value: 'excel', label: 'Export to Excel (all filtered rows)' },
  ];
  readonly navigateTypeOptions: { value: NestedButtonForm['navigateType']; label: string }[] = [
    { value: 'url', label: 'A web address (URL)' },
    { value: 'dashboard', label: 'A published dashboard (id)' },
    { value: 'route', label: 'An app page (route)' },
  ];
  setScale(level: NestedLevelForm, column: NestedColumnForm, scale: string | null): void {
    column.scale = scale === 'auto' || scale === 'K' || scale === 'M' || scale === 'B' ? scale : undefined;
    level.columns = [...level.columns];
    this.onFieldChange();
  }
  setConvertCurrency(level: NestedLevelForm, column: NestedColumnForm, on: boolean): void {
    column.convertCurrency = on || undefined;
    level.columns = [...level.columns];
    this.onFieldChange();
    // Shown at once on the canvas (same live override the Design tab writes).
    this.host.setNestedColumnOverride?.(this.levels.indexOf(level), this.columnKey(column), { convertCurrency: on });
  }
  setWidth(level: NestedLevelForm, column: NestedColumnForm, width: number | string | null): void {
    const px = Number(width);
    column.width = width !== null && width !== '' && Number.isFinite(px) && px > 0 ? Math.round(px) : undefined;
    level.columns = [...level.columns];
    this.onFieldChange();
    // Shown on the canvas straight away; Update Configuration also saves it in the build.
    this.host.setNestedColumnOverride?.(this.levels.indexOf(level), this.columnKey(column), { width: column.width });
  }
  setDecimals(level: NestedLevelForm, column: NestedColumnForm, decimals: number | null): void {
    column.decimals = typeof decimals === 'number' && decimals >= 0 ? decimals : undefined;
    level.columns = [...level.columns];
    this.onFieldChange();
  }
  setParentKeys(level: NestedLevelForm, keys: string[] | null): void {
    level.parentKeys = [...(keys ?? [])];
    this.onFieldChange();
  }
  levelFields(level: NestedLevelForm): string[] {
    // A paired column's second key is a row value too, so rules can test it.
    const items = level.columns.flatMap((column) => [this.columnKey(column), column.secondField ?? '']).filter(Boolean);
    const key = items.join('\u0000');
    const memo = this.levelFieldsMemo.get(level.id);
    if (memo?.key === key) return memo.items;
    this.levelFieldsMemo.set(level.id, { key, items });
    return items;
  }

  addColumn(level: NestedLevelForm): void {
    level.columns = [...level.columns, newColumn(this.host.drillDataset)];
    this.onFieldChange();
  }

  addColumnFilter(column: NestedColumnForm): void {
    column.filters = [...column.filters, newFilter(this.columnDataset(column))];
    this.onFieldChange();
  }

  addLevelFilter(level: NestedLevelForm): void {
    level.filters = [...level.filters, newFilter(this.host.drillDataset)];
    this.onFieldChange();
  }

  removeColumnFilter(column: NestedColumnForm, filter: NestedFilterForm): void {
    column.filters = column.filters.filter((item) => item.id !== filter.id);
    this.host.forgetRowColumns(filter.id);
    this.onFieldChange();
  }

  removeLevelFilter(level: NestedLevelForm, filter: NestedFilterForm): void {
    level.filters = level.filters.filter((item) => item.id !== filter.id);
    this.host.forgetRowColumns(filter.id);
    this.onFieldChange();
  }

  setFilter(filter: NestedFilterForm, patch: Partial<NestedFilterForm>): void {
    const datasetChanged = patch.dataset !== undefined && patch.dataset !== filter.dataset;
    Object.assign(filter, patch);
    if (datasetChanged) {
      filter.field = '';
      if (filter.dataset) this.host.loadColumnsForDataset(filter.dataset);
    }
    this.onFieldChange();
  }

  columnsForFilter(filter: NestedFilterForm): string[] {
    return this.datasetColumns(filter.id, filter.dataset || this.host.drillDataset);
  }

  isFilterLoading(filter: NestedFilterForm): boolean {
    return this.host.isDatasetColumnsLoading(filter.dataset || this.host.drillDataset);
  }

  trackFilter(_index: number, filter: NestedFilterForm): string {
    return filter.id;
  }
  // ── Buttons (per level) ──────────────────────────────────────────────────

  addButton(level: NestedLevelForm): void {
    level.buttons = [...level.buttons, newButton(`Button ${level.buttons.length + 1}`)];
    this.onFieldChange();
  }

  removeButton(level: NestedLevelForm, button: NestedButtonForm): void {
    level.buttons = level.buttons.filter((item) => item.id !== button.id);
    this.onFieldChange();
  }

  moveButton(level: NestedLevelForm, index: number, delta: -1 | 1): void {
    const target = index + delta;
    if (target < 0 || target >= level.buttons.length) return;
    const buttons = [...level.buttons];
    [buttons[index], buttons[target]] = [buttons[target], buttons[index]];
    level.buttons = buttons;
    this.onFieldChange();
  }

  setButton(level: NestedLevelForm, button: NestedButtonForm, patch: Partial<NestedButtonForm>): void {
    // Choosing Export fills in the export API and its request - both stay editable.
    if (patch.action === 'excel' && button.action !== 'excel') {
      if (!button.apiUrl.trim()) patch = { ...patch, apiUrl: EXCEL_EXPORT_DEFAULT_URL };
      if (!button.apiBody.trim() || button.apiBody.includes('{rows}')) patch = { ...patch, apiBody: EXCEL_EXPORT_DEFAULT_BODY };
    }
    Object.assign(button, patch);
    level.buttons = [...level.buttons];
    this.onFieldChange();
  }

  trackButton(_index: number, button: NestedButtonForm): string {
    return button.id;
  }

  /** Arrange a level's columns: the table shows them in this order. */
  moveColumn(level: NestedLevelForm, index: number, delta: -1 | 1): void {
    const target = index + delta;
    if (target < 0 || target >= level.columns.length) return;
    const columns = [...level.columns];
    [columns[index], columns[target]] = [columns[target], columns[index]];
    level.columns = columns;
    this.onFieldChange();
  }

  removeColumn(level: NestedLevelForm, column: NestedColumnForm): void {
    level.columns = level.columns.filter((item) => item.id !== column.id);
    this.host.forgetRowColumns(column.id);
    this.secondColumnsMemo.delete(column.id);
    this.onFieldChange();
  }
  onColumnDatasetChange(level: NestedLevelForm, column: NestedColumnForm, dataset: string | null): void {
    column.dataset = dataset || '';
    column.field = '';
    // The second column belongs to the same dataset, so it goes too.
    column.secondField = '';
    if (column.dataset) this.host.loadColumnsForDataset(column.dataset);
    level.columns = [...level.columns];
    this.onFieldChange();
  }
  onColumnFieldChange(level: NestedLevelForm, column: NestedColumnForm, field: string | null): void {
    column.field = field || '';
    if (column.secondField && column.secondField === column.field) column.secondField = '';
    level.columns = [...level.columns];
    this.onFieldChange();
  }
  /** Data column: pick the SECOND database column shown in the same cell (blank = none). */
  onColumnSecondFieldChange(level: NestedLevelForm, column: NestedColumnForm, field: string | null): void {
    column.secondField = field && field !== column.field ? field : '';
    level.columns = [...level.columns];
    this.onFieldChange();
  }
  /** The second column's choices: the dataset's columns minus the first one
   *  (memoized per column row — ng-select re-renders on a new array reference). */
  private readonly secondColumnsMemo = new Map<string, { key: string; items: string[] }>();
  secondColumnsForColumn(column: NestedColumnForm): string[] {
    const all = this.columnsForColumn(column);
    const key = `${column.field}\u0000${all.join('\u0000')}`;
    const memo = this.secondColumnsMemo.get(column.id);
    if (memo?.key === key) return memo.items;
    const items = all.filter((field) => field !== column.field);
    this.secondColumnsMemo.set(column.id, { key, items });
    return items;
  }
  onColumnLabelChange(column: NestedColumnForm, label: string): void {
    column.label = label;
    this.onFieldChange();
  }
  /** Drop a field from the data pane onto a level → a new column row keeping
   *  the dropped field's own dataset. */
  onColumnFieldDrop(event: DragEvent, level: NestedLevelForm): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    const dataset = mapping.dataset || this.host.drillDataset;
    const duplicate = level.columns.some((column) => column.field === mapping.field && this.columnDataset(column) === dataset);
    if (!duplicate) {
      level.columns = [...level.columns, { ...newColumn(dataset), field: mapping.field }];
      if (dataset) this.host.loadColumnsForDataset(dataset);
    }
    this.onFieldChange();
  }

  /** Generic cell configuration of one column (shared editor). */
  onColumnCellConfigChange(level: NestedLevelForm, column: NestedColumnForm, config: TableCellConfig | undefined): void {
    column.cellConfig = config;
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  trackLevel(_index: number, level: NestedLevelForm): string {
    return level.id;
  }
  trackColumn(_index: number, column: NestedColumnForm): string {
    return column.id;
  }

  // ── DvmBuilderStep contract ────────────────────────────────────────────────

  blockers(): string[] {
    const missing: string[] = [];
    if (!this.host.selectedDatasets.length) missing.push('Dataset');
    if (!this.levels.length) missing.push('At least one level');
    // The backend groups a parent level by its columns, so it cannot be left empty.
    if (this.levels.some((level) => !this.queryColumns(level).length)) {
      missing.push('At least one data or formula column on every level');
    } else if (this.levels.slice(0, -1).some((level) => !this.pickedColumns(level).length)) {
      missing.push('Columns on each level except the last');
    }
    if (this.levels.some((level) => level.columns.some((column) => column.kind === 'formula' && (!column.label.trim() || !String(column.formula ?? '').trim())))) {
      missing.push('Formula name and expression');
    }
    return missing;
  }

  /** Dataset-backed input and dropdown controls are selected by the compute
   * request so their current values can prefill the editors. Aggregation is
   * included only when the user explicitly enables it on that editor column. */
  private queriedEditorColumns(level: NestedLevelForm): Array<Record<string, unknown>> {
    // Dataset columns by lower-case name: an editor's key matches its column
    // whatever the case, and is sent in the dataset's own spelling.
    const real = new Map(this.columnOptions.map((name) => [name.toLowerCase(), name]));
    const picked = new Set(this.pickedColumns(level).map((column) => column.field.toLowerCase()));
    return this.editorColumns(level)
      .flatMap((column) => [this.columnKey(column), pairedField(column, this.columnKey(column))]
        .map((key) => ({ key, field: key ? real.get(key.toLowerCase()) ?? key : key, column, isPrimary: key === this.columnKey(column) })))
      .filter((candidate, at, list) => !!candidate.field
        // A dataset column - or one the user asked to aggregate (e.g. a Planning
        // Status dropdown set to All match), which must be computed either way.
        && (real.has(candidate.field.toLowerCase()) || (candidate.isPrimary && !!candidate.column.aggregate && !!candidate.column.aggregation))
        && !picked.has(candidate.field.toLowerCase())
        && list.findIndex((item) => item.field.toLowerCase() === candidate.field.toLowerCase()) === at)
      .map(({ key, field, column }) => ({
        dataset: this.host.drillDataset,
        field,
        // Rows come back keyed by displayName: the editor's own key, where it reads its value.
        displayName: key,
        label: key,
        fromEditor: true,
        ...this.aggregationPayload(column),
      }));
  }

  /** Dataset column rows with a field picked (blank rows are ignored). */
  private pickedColumns(level: NestedLevelForm): NestedColumnForm[] {
    return level.columns.filter((column) => column.kind === 'data' && !!column.field);
  }

  private formulaColumns(level: NestedLevelForm): NestedColumnForm[] {
    return level.columns.filter((column) => column.kind === 'formula' && !!column.label.trim() && !!String(column.formula ?? '').trim());
  }

  private queryColumns(level: NestedLevelForm): NestedColumnForm[] {
    return [...this.pickedColumns(level), ...this.formulaColumns(level)];
  }

  /** Input / dropdown columns — kept OUT of `columns`: the backend checks every
   *  listed column exists in the dataset. Sent as `inputColumns` instead. */
  private editorColumns(level: NestedLevelForm): NestedColumnForm[] {
    return level.columns.filter((column) => (column.kind === 'input' || column.kind === 'dropdown') && !!this.columnKey(column));
  }

  /** The row key a column is stored under. */
  /**
   * "Minimum / Maximum value" choices: this level's own columns (hidden ones
   * too), by header, valued by the row key each one fills. Memoised per level
   * so ng-select keeps one array between checks.
   */
  boundColumnOptions(level: NestedLevelForm, self: NestedColumnForm): Array<{ label: string; value: string }> {
    const options = level.columns
      .filter((column) => column !== self && column.kind !== 'serial' && !!this.columnKey(column))
      .map((column) => {
        const key = this.columnKey(column);
        const header = column.label.trim();
        const label = header && header !== key ? `${header} (${key})${column.hidden ? ' · hidden' : ''}` : `${key}${column.hidden ? ' · hidden' : ''}`;
        return { label, value: key };
      });
    const memoKey = `${level.id}|${self.id}|${options.map((option) => `${option.value}=${option.label}`).join('~')}`;
    const cached = this.boundOptionsMemo.get(`${level.id}|${self.id}`);
    if (cached?.key === memoKey) return cached.options;
    this.boundOptionsMemo.set(`${level.id}|${self.id}`, { key: memoKey, options });
    return options;
  }
  /** A number typed into the Minimum / Maximum picker (instead of a column). */
  /** A min / max setting (comma-separated columns or numbers) as the select's list - same array while the text is unchanged. */
  private readonly boundLists = new Map<string, string[]>();
  boundList(setting: string | undefined): string[] {
    const text = String(setting ?? '');
    let list = this.boundLists.get(text);
    if (!list) {
      list = text.split(',').map((part) => part.trim()).filter(Boolean);
      this.boundLists.set(text, list);
    }
    return list;
  }

  readonly addBoundNumber = (term: string) => ({ label: term.trim(), value: term.trim() });
  private readonly boundOptionsMemo = new Map<string, { key: string; options: Array<{ label: string; value: string }> }>();

  columnKey(column: NestedColumnForm): string {
    if (column.kind === 'serial') return column.field || '__row_number';
    if (column.kind === 'data') return column.field;
    if (column.kind === 'formula') return column.label.trim() || column.id;
    return column.field || slugKey(column.label) || column.id;
  }

  setColumnRequired(level: NestedLevelForm, column: NestedColumnForm, required: boolean): void {
    column.required = required || undefined;
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  setColumnReadOnly(level: NestedLevelForm, column: NestedColumnForm, readOnly: boolean): void {
    column.readOnly = readOnly || undefined;
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  setNumericOnly(level: NestedLevelForm, column: NestedColumnForm, numericOnly: boolean): void {
    column.numericOnly = numericOnly || undefined;
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  setAggregationEnabled(level: NestedLevelForm, column: NestedColumnForm, enabled: boolean): void {
    column.aggregate = enabled;
    if (enabled && !column.aggregation) column.aggregation = 'SUM';
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  /**
   * Business-key choices for a level: every column of the chosen entity (else
   * the primary dataset), plus the data columns of this level and the levels
   * above it — a level-2 row's key such as cust_type often lives on its parent.
   * Memoised per level so the multi-select gets the SAME array until the list
   * really changes (a fresh array each check made ng-select drop the click).
   */
  bizKeyOptions(level: NestedLevelForm): string[] {
    const index = this.levels.indexOf(level);
    const entity = level.rowSave.entity.trim() || this.host.drillDataset;
    const cached = entity ? this.datasetColumns(`bizkeys-${level.id}`, entity) : [];
    // The primary dataset's list is loaded with the form, even before its cache fills.
    const fromEntity = cached.length || entity !== this.host.drillDataset ? cached : (this.host.drillDatasetColumns ?? []);
    const fromLevels = this.levels.slice(0, index + 1).flatMap((item) => this.pickedColumns(item).map((column) => column.field));
    const items = Array.from(new Set([...fromLevels, ...fromEntity, ...level.rowSave.businessKeys].filter(Boolean)));
    const key = items.join('\u0000');
    const memo = this.bizKeyMemo.get(level.id);
    if (memo?.key === key) return memo.items;
    this.bizKeyMemo.set(level.id, { key, items });
    return items;
  }
  private readonly bizKeyMemo = new Map<string, { key: string; items: string[] }>();

  /** "Columns to send": this level's columns (input / dropdown and data), memoised like bizKeyOptions. */
  payloadColumnOptions(level: NestedLevelForm): string[] {
    const items = Array.from(new Set([...level.columns.map((column) => this.columnKey(column)), ...level.rowSave.payloadColumns].filter(Boolean)));
    const key = items.join('|');
    const memo = this.payloadColumnMemo.get(level.id);
    if (memo?.key === key) return memo.items;
    this.payloadColumnMemo.set(level.id, { key, items });
    return items;
  }
  private readonly payloadColumnMemo = new Map<string, { key: string; items: string[] }>();

  /** Entity picked: load its columns so they can be business keys. */
  setRowSaveEntity(level: NestedLevelForm, entity: string | null): void {
    const value = entity ?? '';
    if (value) this.host.loadColumnsForDataset(value);
    this.setRowSave(level, { entity: value });
  }

  setRowSave(level: NestedLevelForm, patch: Partial<NestedLevelForm['rowSave']>): void {
    level.rowSave = { ...level.rowSave, ...patch };
    this.onFieldChange();
  }

  setColumnKind(level: NestedLevelForm, column: NestedColumnForm, kind: NestedColumnKind): void {
    column.kind = kind;
    if (kind !== 'data') column.dataset = '';
    if (kind !== 'serial' && column.field === '__row_number') column.field = '';
    if (kind === 'serial') {
      column.field = '__row_number';
      column.label = 'S. No.';
      column.secondField = '';
      column.expander = false;
      column.aggregate = false;
      column.aggregation = '';
    }
    if (kind === 'formula') {
      column.aggregate = false;
      column.aggregation = '';
      column.secondField = '';
    }
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  setRowMarker(level: NestedLevelForm, column: NestedColumnForm, marker: 'none' | 'serial'): void {
    if (marker === 'serial') level.columns.forEach((item) => { item.serialNumber = undefined; });
    column.serialNumber = marker === 'serial' || undefined;
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  /** One expand arrow per level: ticking a column moves it there. */
  setExpander(level: NestedLevelForm, column: NestedColumnForm, on: boolean): void {
    level.columns.forEach((item) => (item.expander = on && item.id === column.id));
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  // ── Aggregation settings (config-driven: an option's `params`) ────────────

  private aggregationOption(value: string | undefined): AggregationOption | undefined {
    return value ? (this.host.measureAggregations as AggregationOption[]).find((option) => option.value === value) : undefined;
  }

  /** The extra inputs the column's picked aggregation asks for (none for SUM / AVG …). */
  aggregationParamsFor(column: NestedColumnForm): AggregationParamSpec[] {
    return column.aggregate ? this.aggregationOption(column.aggregation)?.params ?? [] : [];
  }

  aggregationParamValue(column: NestedColumnForm, param: AggregationParamSpec): string | boolean {
    return column.aggregationParams?.[param.key] ?? param.default ?? (param.control === 'checkbox' ? false : '');
  }

  setAggregationParam(level: NestedLevelForm, column: NestedColumnForm, param: AggregationParamSpec, value: string | boolean): void {
    column.aggregationParams = { ...(column.aggregationParams ?? {}), [param.key]: value };
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  /** `{ aggregation, ...its settings }` as the column's payload carries them; {} when not aggregated. */
  private aggregationPayload(column: NestedColumnForm): Record<string, unknown> {
    if (!column.aggregate || !column.aggregation) return {};
    const out: Record<string, unknown> = { aggregation: column.aggregation };
    for (const param of this.aggregationOption(column.aggregation)?.params ?? []) {
      const value = this.aggregationParamValue(column, param);
      if (param.control === 'checkbox') out[param.key] = value === true;
      else if (String(value).trim() !== '') out[param.key] = String(value).trim();
    }
    return out;
  }

  /** The settings of a saved column's aggregation, read back from its payload. */
  private aggregationParamsFromRecord(record: Record<string, unknown>): Record<string, string | boolean> | undefined {
    const out: Record<string, string | boolean> = {};
    for (const param of this.aggregationOption(String(record['aggregation'] ?? ''))?.params ?? []) {
      const value = record[param.key];
      if (value === undefined || value === null) continue;
      out[param.key] = param.control === 'checkbox' ? value === true : String(value);
    }
    return Object.keys(out).length ? out : undefined;
  }

  /** "Use the lowest / highest" of several min / max columns. */
  setBoundPick(level: NestedLevelForm, column: NestedColumnForm, key: 'minPick' | 'maxPick', value: 'lowest' | 'highest'): void {
    column[key] = value;
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  setShowBounds(level: NestedLevelForm, column: NestedColumnForm, show: boolean): void {
    column.showBounds = show ? true : undefined;
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  readonly boundPickOptions = [
    { label: 'Lowest of them', value: 'lowest' },
    { label: 'Highest of them', value: 'highest' },
  ];

  /** "Minimum / Maximum adjustment (%)": signed (-10 lowers, 10 raises); blank = none. */
  setOffsetPct(level: NestedLevelForm, column: NestedColumnForm, key: 'minOffsetPct' | 'maxOffsetPct', value: unknown): void {
    const pct = Number(value);
    column[key] = value === '' || value === null || !Number.isFinite(pct) ? undefined : pct;
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  onColumnTextChange(level: NestedLevelForm, column: NestedColumnForm, key: 'field' | 'options' | 'secondField' | 'separator' | 'formula' | 'aggregation' | 'minValue' | 'maxValue' | 'rangeMessage', value: string): void {
    column[key] = value;
    // A limit picked for the first time starts at -10% (min) / +10% (max) - editable.
    if (key === 'minValue' && value && column.minOffsetPct === undefined) column.minOffsetPct = -10;
    if (key === 'maxValue' && value && column.maxOffsetPct === undefined) column.maxOffsetPct = 10;
    level.columns = [...level.columns];
    this.onFieldChange();
  }

  /** "Order rows by": the configured rules as the table's sort keys (rules without a column are skipped). */
  private sortKeysOf(level: NestedLevelForm): NestedSortKey[] {
    return (level.sortRules ?? []).map(sortRuleToKey).filter((key): key is NestedSortKey => !!key);
  }

  /** The "Sorting" section (after Formatting) is open. */
  sortingOpen = false;

  addSortRule(level: NestedLevelForm): void {
    level.sortRules = [...(level.sortRules ?? []), { columns: [], direction: 'desc' }];
    this.onFieldChange();
  }

  removeSortRule(level: NestedLevelForm, index: number): void {
    level.sortRules = (level.sortRules ?? []).filter((_, i) => i !== index);
    this.onFieldChange();
  }

  setSortRule(level: NestedLevelForm, index: number, patch: Partial<NestedSortRule>): void {
    level.sortRules = (level.sortRules ?? []).map((rule, i) => (i === index ? { ...rule, ...patch } : rule));
    this.onFieldChange();
  }

  /** Move a rule up (it then decides before the one above it). */
  moveSortRule(level: NestedLevelForm, index: number): void {
    const rules = [...(level.sortRules ?? [])];
    if (index <= 0 || index >= rules.length) return;
    [rules[index - 1], rules[index]] = [rules[index], rules[index - 1]];
    level.sortRules = rules;
    this.onFieldChange();
  }

  sortOptions(level: NestedLevelForm): string[] {
    return this.queryColumns(level).map((column) => column.kind === 'formula'
      ? column.label.trim()
      : (column.label.trim() || column.field));
  }

  /** One `key = [key]` filter per picked parent column; the dataset is the
   *  one that column reads from on its own level. */
  private parentKeyFilters(level: NestedLevelForm, index: number): NestedTableQueryFilter[] {
    if (index === 0) return [];
    const parents = this.levels.slice(0, index).flatMap((item) => item.columns);
    return (level.parentKeys ?? []).filter(Boolean).map((key) => {
      const column = parents.find((item) => item.field === key || item.secondField === key);
      return {
        dataset: (column ? this.columnDataset(column) : '') || this.host.drillDataset,
        field: key,
        operator: '=',
        value: `[${key}]`,
      };
    });
  }

  private filterPayload(filters: NestedFilterForm[]): NestedTableQueryFilter[] {
    return filters
      .filter((filter) => !!filter.field.trim() && !!filter.operator.trim())
      .map((filter) => ({
        dataset: filter.dataset || this.host.drillDataset,
        field: filter.field.trim(),
        operator: filter.operator.trim(),
        value: filter.value,
      }));
  }

  private levelQuery(level: NestedLevelForm, index: number): NestedTableLevelQuery {
    const columns = [
      ...this.pickedColumns(level).flatMap((column) => {
        const dataset = this.columnDataset(column);
        const filters = this.filterPayload(column.filters);
        const primary = {
          dataset,
          field: column.field,
          displayName: column.label.trim() || column.field,
          ...this.aggregationPayload(column),
          ...(filters.length ? { filters } : {}),
        };
        const second = pairedField(column, column.field);
        return second ? [primary, { dataset, field: second, displayName: second }] : [primary];
      }),
      ...this.formulaColumns(level).map((column) => ({
        displayName: column.label.trim(),
        formula: String(column.formula ?? '').trim(),
      })),
      ...this.queriedEditorColumns(level).map((column) => ({
        dataset: String(column['dataset'] ?? ''),
        field: String(column['field'] ?? ''),
        displayName: String(column['displayName'] ?? column['field'] ?? ''),
        ...(column['aggregation'] ? { aggregation: String(column['aggregation']), ...this.aggregationParamsFromRecord(column) } : {}),
      })),
    ];
    const filters = [...this.parentKeyFilters(level, index), ...this.filterPayload(level.filters)];
    return {
      componentType: 'TABLE',
      title: index === 0 ? this.host.title : (level.title || this.host.title),
      columns,
      ...(filters.length ? { filters } : {}),
      ...(level.sortField ? { sorting: [{ field: level.sortField, direction: level.sortDirection }] } : {}),
      pagination: { enabled: level.paginationEnabled },
    };
  }

  buildPayload(): Record<string, unknown> {
    const dataset = this.host.selectedDatasets[0] ?? '';
    // Every dataset the table reads from: the primary one plus any a column
    // row points at (the backend joins them, as for the Normal table).
    const datasets = Array.from(new Set([
      ...this.host.selectedDatasets,
      ...this.levels.flatMap((level) => this.pickedColumns(level).map((column) => this.columnDataset(column))),
    ].filter(Boolean)));
    const levelQueries = this.levels.map((level, index) => this.levelQuery(level, index));
    // Saved as the configured PATH, not a full URL: the table resolves it against the
    // server it is viewed on (a full URL would pin it to the machine it was built on).
    const levelApiUrl = String(this.host.layout.apiEndpoints?.generateConfigApi ?? '').trim() || '/components/compute';
    return {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: 'CHART',
      chartType: 'NESTED_TABLE',
      title: this.host.title,
      dataset,
      datasets,
      levelApiUrl,
      levelQueries,
      ...(this.rowsKey ? { rowsKey: this.rowsKey } : {}),
      ...(this.subtitle ? { subtitle: this.subtitle } : {}),
      levels: this.levels.map((level, index) => ({
        title: level.title,
        // Children always go under a name no column uses (the backend's own default).
        ...(index < this.levels.length - 1 ? { childrenField: CHILDREN_FIELD } : {}),
        // Display order of every column, editable ones included.
        columnOrder: level.columns.map((column) => this.columnKey(column)).filter(Boolean),
        filters: this.filterPayload(level.filters),
        ...(index > 0 && level.parentKeys?.length ? { parentKeys: [...level.parentKeys] } : {}),
        ...(level.sortField ? { sorting: [{ field: level.sortField, direction: level.sortDirection }] } : {}),
        ...(this.sortKeysOf(level).length ? { sortBy: this.sortKeysOf(level) } : {}),
        ...(level.headerSort ? { headerSort: true } : {}),
        pagination: { enabled: level.paginationEnabled },
        ...(level.rowSave.enabled && level.rowSave.url.trim()
          ? {
              rowSave: {
                label: level.rowSave.label.trim() || 'Save',
                url: level.rowSave.url.trim(),
                ...(level.rowSave.entity.trim() ? { entity: level.rowSave.entity.trim() } : {}),
                businessKeys: level.rowSave.businessKeys.filter(Boolean),
                payloadColumns: level.rowSave.payloadColumns.filter(Boolean),
              },
            }
          : {}),
        ...(level.buttons.length ? { actions: level.buttons.filter((button) => button.label.trim()).map(buttonPayload) } : {}),
        ...(level.columns.some((column) => column.serialNumber || column.kind === 'serial')
          ? { serialColumns: level.columns.filter((column) => column.serialNumber || column.kind === 'serial').map((column) => ({ field: column.kind === 'serial' ? this.columnKey(column) : column.field, label: column.kind === 'serial' ? column.label || 'S. No.' : 'S. No.' })) }
          : {}),
        ...(this.editorColumns(level).length
          ? {
              inputColumns: this.editorColumns(level).map((column) => ({
                field: this.columnKey(column),
                label: column.label || this.columnKey(column),
                type: column.kind,
                ...(column.kind === 'dropdown' ? { options: splitOptions(column.options) } : {}),
                ...this.aggregationPayload(column),
                ...(column.expander ? { expander: true } : {}),
                ...(column.required ? { required: true } : {}),
                ...(column.readOnly ? { readOnly: true } : {}),
                ...(column.kind === 'input' && typeof column.decimals === 'number' ? { decimals: column.decimals } : {}),
                ...(column.kind === 'input' && column.numericOnly ? { inputType: 'number' as const } : {}),
                ...(column.kind === 'input' && column.numericOnly ? { numericOnly: true } : {}),
                ...(column.kind === 'input' && column.minValue?.trim() ? { minValue: column.minValue.trim() } : {}),
                ...(column.kind === 'input' && column.maxValue?.trim() ? { maxValue: column.maxValue.trim() } : {}),
                ...(column.kind === 'input' && typeof column.minOffsetPct === 'number' ? { minOffsetPct: column.minOffsetPct } : {}),
                ...(column.kind === 'input' && typeof column.maxOffsetPct === 'number' ? { maxOffsetPct: column.maxOffsetPct } : {}),
                ...(column.kind === 'input' && column.rangeMessage?.trim() ? { rangeMessage: column.rangeMessage.trim() } : {}),
                ...(column.kind === 'input' && column.minPick ? { minPick: column.minPick } : {}),
                ...(column.kind === 'input' && column.maxPick ? { maxPick: column.maxPick } : {}),
                ...(column.kind === 'input' && column.showBounds === true ? { showBounds: true } : {}),
                ...(typeof column.width === 'number' && column.width > 0 ? { width: column.width } : {}), ...(column.convertCurrency ? { convertCurrency: true } : {}),
                ...(column.cellConfig ? { cellConfig: column.cellConfig } : {}),
                ...pairPayload(column, this.columnKey(column)),
              })),
            }
          : {}),
        ...(this.queryColumns(level).length
          ? {
              columns: [
                ...this.pickedColumns(level).flatMap((column) => {
                  // Same cross-dataset column contract as the Normal table:
                  // {dataset, field, displayName}. `label` is kept for the
                  // renderer, which reads it as the header.
                  const dataset = this.columnDataset(column);
                  const primary = {
                    dataset,
                    field: column.field,
                    displayName: column.label || column.field,
                    label: column.label || column.field,
                    ...this.aggregationPayload(column),
                    ...(this.filterPayload(column.filters).length ? { filters: this.filterPayload(column.filters) } : {}),
                    ...(column.expander ? { expander: true } : {}),
                    ...(column.hidden ? { hidden: true } : {}),
                    ...(typeof column.decimals === 'number' ? { decimals: column.decimals } : {}),
                    ...(column.scale ? { scale: column.scale } : {}),
                    ...(typeof column.width === 'number' && column.width > 0 ? { width: column.width } : {}), ...(column.convertCurrency ? { convertCurrency: true } : {}),
                    ...(column.cellConfig ? { cellConfig: column.cellConfig } : {}),
                    ...pairPayload(column, column.field),
                  };
                  const second = pairedField(column, column.field);
                  if (!second) return [primary];
                  // A paired column's SECOND database column is listed too, so
                  // the backend selects (and groups by) it — hidden, because the
                  // first column draws both values in its own cell.
                  return [primary, { dataset, field: second, displayName: second, label: second, hidden: true, pairedWith: column.field }];
                }),
                ...this.formulaColumns(level).map((column) => ({
                  field: column.label.trim(),
                  displayName: column.label.trim(),
                  label: column.label.trim(),
                  formula: String(column.formula ?? '').trim(),
                  ...(column.expander ? { expander: true } : {}),
                  ...(column.hidden ? { hidden: true } : {}),
                  ...(typeof column.decimals === 'number' ? { decimals: column.decimals } : {}),
                    ...(column.scale ? { scale: column.scale } : {}),
                  ...(typeof column.width === 'number' && column.width > 0 ? { width: column.width } : {}), ...(column.convertCurrency ? { convertCurrency: true } : {}),
                  ...(column.cellConfig ? { cellConfig: column.cellConfig } : {}),
                })),
                ...this.queriedEditorColumns(level),
              ],
            }
          : {}),
      })),
      ...this.host.chartTargetsPayload(),
      ...this.host.headerFormatPayload(),
    };
  }

  /** Generate only the first level. Child level TABLE requests are executed by
   * the renderer when a user expands a customer / segment row. */
  generate(payload: Record<string, unknown>, url: string): boolean {
    const queries = Array.isArray(payload['levelQueries']) ? payload['levelQueries'] as NestedTableLevelQuery[] : [];
    const initial = queries[0];
    if (!initial) return false;
    const computeUrl = rebaseSelfServiceApiUrl(payload['levelApiUrl'] as string | undefined) || url;
    (this.host.requestGenerate?.(computeUrl, initial as unknown as Record<string, unknown>)
      ?? this.http.post<Record<string, unknown>>(computeUrl, initial)).subscribe({
      next: (response) => {
        this.host.isGenerating = false;
        const updated = this.host.addTileFromResponse({ data: nestedQueryRows(response, this.rowsKey) }, payload);
        this.ui.showSuccess(updated ? 'Nested table updated on the dashboard.' : 'Nested table added to the dashboard.');
      },
      error: (error: HttpErrorResponse) => {
        this.host.isGenerating = false;
        this.host.generateError = error.error?.message || error.message || 'Unable to generate the nested table.';
        this.ui.showError(this.host.generateError);
      },
    });
    return true;
  }

  /** Generate response becomes the level-0 rows; later levels load on demand. */
  tileFromResponse(base: DashboardTile, response: Record<string, unknown> | null): DashboardTile {
    return { ...base, nestedTableData: this.dataFor(base, response, base.format ?? {}) };
  }

  /** A filter recomputed this table on the backend. */
  applyRecomputeResult(tile: DashboardTile, { result, format }: TileRecomputeResult): boolean {
    tile.nestedTableData = this.dataFor(tile, result, format);
    return true;
  }

  /** The tile's Format popup changed: same rows, new look. */
  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    if (!tile.nestedTableData) return;
    tile.nestedTableData = this.dataFor(tile, tile.nestedTableData.staticData ?? [], format);
  }

  private dataFor(tile: DashboardTile, response: unknown, format: Record<string, unknown>) {
    const data = nestedTableData(nestedTableRows(response), tile.payload, tile.title, format);
    data.widgetId = 'dvm-tile-nested-' + tile.id;
    return data;
  }

  loadFromTile(_tile: DashboardTile, payload: Record<string, unknown>): void {
    this.rowsKey = String(payload['rowsKey'] ?? '');
    this.subtitle = String(payload['subtitle'] ?? '');
    const primary = String(payload['dataset'] ?? this.host.drillDataset ?? '');

    const saved = Array.isArray(payload['levels'])
      ? (payload['levels'] as Array<Record<string, unknown>>)
      : [];
    const savedQueries = Array.isArray(payload['levelQueries'])
      ? payload['levelQueries'] as Array<Record<string, unknown>>
      : [];
    this.levels = saved.length
      ? saved.map((level, index): NestedLevelForm => {
          const savedColumns = Array.isArray(level['columns'])
            ? (level['columns'] as Array<Record<string, unknown> | string>)
                // Queried only to prefill an input / dropdown column — not a card of its own.
                .filter((entry) => typeof entry === 'string' || entry?.['fromEditor'] !== true)
            : [];
          // The hidden entry buildPayload adds for a paired column's second
          // database column is not a column row of its own — the first column
          // keeps it as its "Second column".
          const pairedSeconds = new Set(
            savedColumns.map((entry) => (typeof entry === 'string' ? '' : String(entry?.['secondField'] ?? ''))).filter(Boolean),
          );
          const dataColumns = savedColumns
            .filter((entry) => typeof entry === 'string' || !(entry?.['hidden'] === true && pairedSeconds.has(String(entry['field'] ?? ''))))
            .map((entry): NestedColumnForm => {
              // Older saves carried bare field names / {field,label} only.
              if (typeof entry === 'string') return { ...newColumn(''), field: entry };
              const record = entry ?? {};
              const dataset = String(record['dataset'] ?? '');
              const field = String(record['field'] ?? '');
              const label = String(record['displayName'] ?? record['label'] ?? '');
              if (typeof record['formula'] === 'string' && String(record['formula']).trim()) {
                return {
                  ...newColumn('', 'formula'),
                  field: '',
                  label: label || field,
                  formula: String(record['formula']),
                  expander: record['expander'] === true,
                  hidden: record['hidden'] === true || undefined,
                  decimals: typeof record['decimals'] === 'number' ? record['decimals'] : undefined,
                scale: (['auto', 'K', 'M', 'B'] as const).find((option) => option === record['scale']),
                width: typeof record['width'] === 'number' && record['width'] > 0 ? record['width'] : undefined, convertCurrency: record['convertCurrency'] === true || undefined,
                  cellConfig: normalizeTableCellConfig(record['cellConfig']),
                };
              }
              return {
                ...newColumn(dataset && dataset !== primary ? dataset : ''),
                field,
                label: label === field ? '' : label,
                aggregate: !!record['aggregation'],
                aggregation: String(record['aggregation'] ?? ''),
                aggregationParams: this.aggregationParamsFromRecord(record),
                filters: filtersFromPayload(record['filters'], dataset || primary),
                expander: record['expander'] === true,
                hidden: record['hidden'] === true || undefined,
                decimals: typeof record['decimals'] === 'number' ? record['decimals'] : undefined,
                scale: (['auto', 'K', 'M', 'B'] as const).find((option) => option === record['scale']),
                width: typeof record['width'] === 'number' && record['width'] > 0 ? record['width'] : undefined, convertCurrency: record['convertCurrency'] === true || undefined,
                cellConfig: normalizeTableCellConfig(record['cellConfig']),
                ...pairFromPayload(record),
              };
            })
            .filter((column) => column.kind === 'formula' ? !!column.label : !!column.field);
          const editorColumns = Array.isArray(level['inputColumns'])
            ? (level['inputColumns'] as Array<Record<string, unknown>>).map((record): NestedColumnForm => ({
                ...newColumn('', record['type'] === 'dropdown' ? 'dropdown' : 'input'),
                field: String(record['field'] ?? ''),
                label: String(record['label'] ?? ''),
                options: Array.isArray(record['options']) ? (record['options'] as unknown[]).map(String).join(', ') : '',
                aggregate: !!record['aggregation'],
                aggregation: String(record['aggregation'] ?? ''),
                aggregationParams: this.aggregationParamsFromRecord(record),
                expander: record['expander'] === true,
                required: record['required'] === true || undefined,
                readOnly: record['readOnly'] === true || undefined,
                numericOnly: record['numericOnly'] === true || record['inputType'] === 'number' || undefined,
                minValue: typeof record['minValue'] === 'string' ? record['minValue'] : undefined,
                maxValue: typeof record['maxValue'] === 'string' ? record['maxValue'] : undefined,
                minOffsetPct: typeof record['minOffsetPct'] === 'number' ? record['minOffsetPct'] : undefined,
                maxOffsetPct: typeof record['maxOffsetPct'] === 'number' ? record['maxOffsetPct'] : undefined,
                rangeMessage: typeof record['rangeMessage'] === 'string' ? record['rangeMessage'] : undefined,
                minPick: record['minPick'] === 'lowest' || record['minPick'] === 'highest' ? record['minPick'] : undefined,
                maxPick: record['maxPick'] === 'lowest' || record['maxPick'] === 'highest' ? record['maxPick'] : undefined,
                showBounds: record['showBounds'] === true ? true : undefined,
                decimals: typeof record['decimals'] === 'number' ? record['decimals'] : undefined,
                width: typeof record['width'] === 'number' && record['width'] > 0 ? record['width'] : undefined, convertCurrency: record['convertCurrency'] === true || undefined,
                cellConfig: normalizeTableCellConfig(record['cellConfig']),
                ...pairFromPayload(record),
              }))
            : [];
          // Saved display order (editable columns sit among the data ones).
          const order = Array.isArray(level['columnOrder']) ? (level['columnOrder'] as unknown[]).map(String) : [];
          const rank = (column: NestedColumnForm) => {
            const at = order.indexOf(this.columnKey(column));
            return at < 0 ? Number.MAX_SAFE_INTEGER : at;
          };
          const serialColumns = Array.isArray(level['serialColumns']) ? level['serialColumns'] as Array<Record<string, unknown>> : [];
          const serialFields = new Set(serialColumns.map((record) => String(record['field'] ?? '')).filter(Boolean));
          const columns = [...dataColumns, ...editorColumns].sort((a, b) => rank(a) - rank(b));
          if (serialColumns.length) {
            const marked = columns.filter((column) => serialFields.has(this.columnKey(column)));
            (marked.length ? marked : columns.slice(0, 1)).forEach((column) => { column.serialNumber = true; });
          }
          // Before the tick box, "Expands into" named the field the children went
          // under. When that was a real column, that column is the arrow column.
          const legacy = String(level['childrenField'] ?? '');
          if (legacy && legacy !== CHILDREN_FIELD && !columns.some((column) => column.expander)) {
            const hit = columns.find((column) => column.field === legacy);
            if (hit) hit.expander = true;
          }
          const buttons = Array.isArray(level['actions'])
            ? (level['actions'] as Array<Record<string, unknown>>).map(buttonFromPayload)
            : [];
          const save = (level['rowSave'] ?? null) as Record<string, unknown> | null;
          const query = savedQueries[index] ?? {};
          const sorting = (Array.isArray(level['sorting']) ? level['sorting'] : query['sorting']) as Array<Record<string, unknown>> | undefined;
          const pagination = (level['pagination'] ?? query['pagination']) as Record<string, unknown> | undefined;
          return {
            id: `lvl_${index}_${Date.now().toString(36)}`,
            title: String(level['title'] ?? `Level ${index + 1}`),
            childrenField: index < saved.length - 1 ? CHILDREN_FIELD : '',
            columns,
            buttons,
            filters: filtersFromPayload(level['filters'] ?? query['filters'], primary),
            parentKeys: Array.isArray(level['parentKeys']) ? (level['parentKeys'] as unknown[]).map(String).filter(Boolean) : [],
            sortField: String(sorting?.[0]?.['field'] ?? ''),
            sortDirection: String(sorting?.[0]?.['direction'] ?? 'DESC').toUpperCase() === 'ASC' ? 'ASC' : 'DESC',
            sortRules: (Array.isArray(level['sortBy']) ? level['sortBy'] as NestedSortKey[] : []).map(sortKeyToRule),
            headerSort: level['headerSort'] === true,
            paginationEnabled: pagination?.['enabled'] === true,
            rowSave: save
              ? {
                  enabled: true,
                  label: String(save['label'] ?? 'Save'),
                  url: String(save['url'] ?? ''),
                  entity: String(save['entity'] ?? ''),
                  businessKeys: Array.isArray(save['businessKeys']) ? (save['businessKeys'] as unknown[]).map(String) : [],
                  payloadColumns: Array.isArray(save['payloadColumns']) ? (save['payloadColumns'] as unknown[]).map(String) : [],
                }
              : newRowSave(),
          };
        })
      : [newLevel('Level 1', 'children'), newLevel('Level 2', '')];
    // Warm the column lists of every referenced dataset so the pickers resolve.
    new Set(this.levels.flatMap((level) => [
      ...level.columns.map((column) => this.columnDataset(column)),
      ...level.filters.map((filter) => filter.dataset || primary),
      ...level.columns.flatMap((column) => column.filters.map((filter) => filter.dataset || primary)),
    ]).filter(Boolean))
      .forEach((dataset) => this.host.loadColumnsForDataset(dataset));
  }

  reset(): void {
    this.rowsKey = '';
    this.subtitle = '';
    this.levels = [newLevel('Level 1', 'children'), newLevel('Level 2', '')];
    this.levelFieldsMemo.clear();
    this.secondColumnsMemo.clear();
  }
}

/** Deepest nesting the form offers. The renderer and the per-level Format
 *  groups follow whatever depth is built; this is only the form's cap. */
export const MAX_NESTED_LEVELS = 10;

let levelCounter = 0;
let columnCounter = 0;
let filterCounter = 0;

function newLevel(title: string, childrenField: string): NestedLevelForm {
  levelCounter += 1;
  return {
    id: `lvl_${levelCounter}`,
    title,
    childrenField,
    columns: [],
    buttons: [],
    rowSave: newRowSave(),
    filters: [],
    parentKeys: [],
    sortField: '',
    sortDirection: 'DESC',
    sortRules: [],
    headerSort: false,
    paginationEnabled: false,
  };
}

function newColumn(dataset: string, kind: NestedColumnKind = 'data'): NestedColumnForm {
  columnCounter += 1;
  return { id: `nested-column-${columnCounter}`, kind, dataset, field: '', label: '', filters: [], aggregation: '' };
}

function newFilter(dataset: string): NestedFilterForm {
  filterCounter += 1;
  return { id: `nested-filter-${filterCounter}`, dataset, field: '', operator: '=', value: '' };
}

function filtersFromPayload(value: unknown, primaryDataset: string): NestedFilterForm[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => {
    const record = (entry ?? {}) as Record<string, unknown>;
    return {
      ...newFilter(String(record['dataset'] ?? primaryDataset)),
      field: String(record['field'] ?? ''),
      operator: String(record['operator'] ?? '='),
      value: String(record['value'] ?? ''),
    };
  });
}

/** A paired column's second key, when set and different from its first. */
function pairedField(column: NestedColumnForm, primaryKey: string): string {
  const second = String(column.secondField ?? '').trim();
  return second && second !== primaryKey ? second : '';
}

/** `secondField` / `separator` as the renderer reads them (nothing for a single-value column). */
function pairPayload(column: NestedColumnForm, primaryKey: string): Record<string, unknown> {
  const secondField = pairedField(column, primaryKey);
  if (!secondField) return {};
  const separator = String(column.separator ?? '').trim();
  return { secondField, ...(separator ? { separator } : {}) };
}

function pairFromPayload(record: Record<string, unknown>): Pick<NestedColumnForm, 'secondField' | 'separator'> {
  const secondField = String(record['secondField'] ?? '').trim();
  if (!secondField) return {};
  return { secondField, separator: String(record['separator'] ?? '').trim() };
}

/** "Price Justification" → "price_justification" — an editable column's row key. */
function slugKey(text: string): string {
  return String(text ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function newRowSave(): NestedLevelForm['rowSave'] {
  return { enabled: false, label: 'Save', url: '', entity: '', businessKeys: [], payloadColumns: [] };
}

let buttonCounter = 0;
function newButton(label: string): NestedButtonForm {
  buttonCounter += 1;
  return {
    id: `btn_${Date.now().toString(36)}_${buttonCounter}`, label, style: 'outline', action: 'navigate',
    sheetName: 'Sheet1', filterColumns: [], navigateType: 'url', navigateTarget: '', navigateNewTab: false, apiMethod: 'POST', apiUrl: '', apiBody: '',
  };
}

/** What the table and the runtime need — only the chosen action's settings. */
function buttonPayload(button: NestedButtonForm): Record<string, unknown> {
  return {
    // The table's built-in CSV export answers to the id "export".
    id: button.action === 'export' ? 'export' : button.id,
    label: button.label.trim(),
    style: button.style,
    action: button.action,
    ...(button.action === 'navigate'
      ? { navigateType: button.navigateType, navigateTarget: button.navigateTarget, navigateNewTab: button.navigateNewTab }
      : {}),
    ...(button.action === 'api' ? { apiMethod: button.apiMethod, apiUrl: button.apiUrl, apiBody: button.apiBody } : {}),
    ...(button.action === 'excel'
      ? {
          sheetName: button.sheetName.trim() || 'Sheet1',
          apiUrl: button.apiUrl.trim() || EXCEL_EXPORT_DEFAULT_URL,
          apiBody: button.apiBody.trim() || EXCEL_EXPORT_DEFAULT_BODY,
          ...(button.filterColumns.length ? { filterColumns: [...button.filterColumns] } : {}),
        }
      : {}),
  };
}

function buttonFromPayload(record: Record<string, unknown>): NestedButtonForm {
  const base = newButton(String(record['label'] ?? ''));
  const action = record['action'] === 'api' || record['action'] === 'export' || record['action'] === 'excel'
    ? record['action'] : record['id'] === 'export' ? 'export' : 'navigate';
  return {
    ...base,
    style: record['style'] === 'filled' ? 'filled' : 'outline',
    action,
    navigateType: record['navigateType'] === 'dashboard' || record['navigateType'] === 'route' ? record['navigateType'] : 'url',
    navigateTarget: String(record['navigateTarget'] ?? ''),
    navigateNewTab: record['navigateNewTab'] === true,
    apiMethod: (['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const).find((method) => method === record['apiMethod']) ?? 'POST',
    // An Export button saved before it carried its call gets the defaults.
    apiUrl: String(record['apiUrl'] ?? '') || (action === 'excel' ? EXCEL_EXPORT_DEFAULT_URL : ''),
    apiBody: String(record['apiBody'] ?? '') || (action === 'excel' ? EXCEL_EXPORT_DEFAULT_BODY : ''),
    sheetName: String(record['sheetName'] ?? '') || 'Sheet1',
    filterColumns: Array.isArray(record['filterColumns']) ? (record['filterColumns'] as unknown[]).map(String).filter(Boolean) : [],
  };
}

/** "Approve, Reject" → ['Approve', 'Reject']. */
function splitOptions(text: string | undefined): string[] {
  return String(text ?? '').split(',').map((option) => option.trim()).filter(Boolean);
}
